import 'isomorphic-fetch';
import { get, set, isArray, merge, filter, isString } from 'lodash';

const symbols = {
  events: Symbol('Events instance property'),
  data: Symbol('Configs store property'),
};

const loadJSON = url => fetch(url).then(response => response.json());
const loadJSONAll = urls => (
  Promise.all(urls.map(loadJSON))
    .then(files => merge({}, ...files))
);

const localStorageKeyPrefixMarker = 'tulip-config-cache::';

export default class Configs {
  constructor(events) {
    this[symbols.events] = events;
  }

  async init(options = {}) {
    if (options.data) {
      this.setConfigData(options.data);
    }

    this[symbols.loader] = options.loader;

    if (options.url) {
      await this.loadJSON(options.url);
    }

    return this;
  }


  // Clear all configs in localStorage
  clearAll() {
    const localStorageAllKeys = Object.keys(localStorage);
    const configKeys = localStorageAllKeys
                                    .filter(key => key.indexOf(localStorageKeyPrefixMarker) === 0);
    configKeys.forEach((key) => {
      localStorage.removeItem(key);
    });
  }

  buildLocalStorageConfigKey(configPath) {
    const domain = configPath.split('.', 1).shift();
    return localStorageKeyPrefixMarker + domain;
  }

  // Get all config values from localStorage
  getAll() {
    const configData = {};
    const localStorageAllKeys = Object.keys(localStorage);
    const configKeys = localStorageAllKeys
                                    .filter(key => key.indexOf(localStorageKeyPrefixMarker) === 0);
    configKeys.forEach((key) => {
      const stripped = key.substr(localStorageKeyPrefixMarker.length);
      configData[stripped] = JSON.parse(localStorage.getItem(key));
    });

    return configData;
  }

  // Set config data object to localStorage where keys will be the localStorage key
  // prepended with the prefix marker 'tulip-config-cache::'
  setConfigData(configData = {}) {
    const keys = Object.keys(configData);
    keys.forEach((key) => {
      this.set(key, configData[key]);
    });
  }

  // Get config value from localStorage
  get(configPath, defaultValue) {
    if (!isString(configPath)) {
      throw new TypeError(`${configPath} is not a String`);
    }
    const data = localStorage.getItem(this.buildLocalStorageConfigKey(configPath));
    if (data) {
      const parsedData = {};
      const domain = configPath.split('.', 1).shift();
      parsedData[domain] = JSON.parse(data);
      return get(parsedData, configPath, defaultValue);
    }
    return defaultValue;
  }

  // Checks a feature flag by resource and setting
  isFeatureEnabled(resource, setting) {
    return this.get(`features.${resource}.${setting}`, false);
  }

  // Set config value to localStorage
  set(configPath, value = null) {
    if (!isString(configPath)) {
      throw new TypeError(`${configPath} is not a String`);
    }
    const localStorageConfigKey = this.buildLocalStorageConfigKey(configPath);
    const data = localStorage.getItem(localStorageConfigKey);
    let dataToSet = value;
    if (data) {
      const parsedData = {};
      const domain = configPath.split('.', 1).shift();
      parsedData[domain] = JSON.parse(data);
      set(parsedData, configPath, value);
      dataToSet = parsedData[domain];
    }
    localStorage.setItem(localStorageConfigKey, JSON.stringify(dataToSet));
  }

  // Load config values to localStorage
  async load(resources, refreshCache = false) {
    if (!isString(resources) && !isArray(resources)) {
      return null;
    }

    const loader = this[symbols.loader];
    const events = this[symbols.events];
    const configPathlist = isArray(resources) ? resources : [resources];

    // validate resources for '/' character
    configPathlist.forEach((configPath) => {
      if (configPath.indexOf('/') > -1) {
        throw new Error(`Invalid config path: ${configPath}`);
      }
    });

    if (refreshCache) {
      this.clearAll();
    }

    let loadedConfigData = null;

    // Check if configs are cached in localStorage
    const dataListToFetchFromRemote = [];
    configPathlist.forEach((configPath) => {
      const data = localStorage.getItem(this.buildLocalStorageConfigKey(configPath));
      if (data) {
        // Get from cache
        const localData = {};
        const domain = configPath.split('.', 1).shift();
        localData[domain] = JSON.parse(data);
        loadedConfigData = merge(loadedConfigData, localData);
      } else {
        // Store list of config paths to fetch from remote
        dataListToFetchFromRemote.push(configPath);
      }
    });

    const remoteData = await loader.load(dataListToFetchFromRemote);
    // Load configs to memory
    loadedConfigData = merge(loadedConfigData, remoteData);

    // Set data in localStorage
    this.setConfigData(remoteData);

    // Clear loaded resources when user logs out
    events.once(events.types.onLogout, () => {
      this.clearAll();
    });

    return loadedConfigData;
  }

  async loadJSON(url) {
    if (!url) {
      throw new Error('Invalid configs url!');
    }

    const data = await loadJSONAll(isArray(url) ? filter(url) : [url]);

    this.setConfigData(data);

    return data;
  }
}



// WEBPACK FOOTER //
// ./src/Configs/Configs.js